import React, { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { personalInfo } from '../data/mock';
import { Github, Linkedin, Instagram, Heart, ArrowUp, Sparkles, Code, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Footer = () => {
  const footerRef = useRef(null);
  const { currentColors } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Enhanced footer animation with 3D effects
            anime({
              targets: footerRef.current.children,
              opacity: [0, 1],
              translateY: [40, 0],
              rotateX: [45, 0],
              scale: [0.9, 1],
              duration: 800,
              delay: anime.stagger(200),
              easing: 'easeOutElastic(1, .6)'
            });

            // Add floating animation to footer elements
            setTimeout(() => {
              const footerElements = footerRef.current.querySelectorAll('.footer-float');
              footerElements.forEach((el, index) => {
                anime({
                  targets: el,
                  translateY: [0, -5, 0],
                  duration: 3000 + (index * 500),
                  loop: true,
                  easing: 'easeInOutSine',
                  delay: index * 200
                });
              });
            }, 1000);

            // Animate background particles
            setTimeout(() => {
              const particles = document.querySelectorAll('.footer-particle');
              particles.forEach((particle, index) => {
                anime({
                  targets: particle,
                  translateY: [0, -30, 0],
                  translateX: [0, Math.random() * 20 - 10, 0],
                  opacity: [0.5, 1, 0.5],
                  scale: [1, 1.2, 1],
                  duration: 4000 + (Math.random() * 2000),
                  delay: index * 300,
                  loop: true,
                  easing: 'easeInOutSine'
                });
              });
            }, 1200);

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    // Animate scroll to top button
    anime({
      targets: '.scroll-top-btn',
      scale: [1, 1.2, 1],
      rotateZ: [0, 360],
      duration: 500,
      easing: 'easeOutQuart'
    });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'About', href: '#about' },
    { label: 'Skills', href: '#skills' },
    { label: 'Projects', href: '#projects' },
    { label: 'Contact', href: '#contact' }
  ];

  const socialLinks = [
    { icon: <Github size={20} />, url: personalInfo.social.github, label: 'GitHub', color: '#333' },
    { icon: <Linkedin size={20} />, url: personalInfo.social.linkedin, label: 'LinkedIn', color: '#0077B5' },
    { icon: <Instagram size={20} />, url: personalInfo.social.instagram, label: 'Instagram', color: '#E4405F' }
  ];

  const techStack = [
    { name: 'React', icon: <Code size={16} /> },
    { name: 'Anime.js', icon: <Sparkles size={16} /> },
    { name: 'Tailwind CSS', icon: <Zap size={16} /> }
  ];

  return (
    <footer 
      className="relative z-10 transition-colors duration-500"
      style={{ 
        backgroundColor: currentColors.bg === '#F2F2F2' ? '#0f0f10' : '#1a1a1b',
        color: '#FFFFFF'
      }}
    >
      <div className="container mx-auto px-6 py-20">
        <div ref={footerRef} className="grid md:grid-cols-4 gap-12">
          {/* Enhanced Brand Section */}
          <div className="footer-float opacity-0">
            <div className="flex items-center gap-3 mb-6">
              <div 
                className="p-2 rounded-full"
                style={{ backgroundColor: `${currentColors.accent}20` }}
              >
                <Sparkles size={20} style={{ color: currentColors.accent }} />
              </div>
              <div 
                className="font-mono text-xl font-bold uppercase tracking-wide cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={scrollToTop}
              >
                Kevin Panchal
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Cross Platform & Web Developer + Data Analyst. Creating intelligent solutions 
              that bridge data insights with exceptional user experiences through innovative 
              technology and cutting-edge development practices.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-full transition-all duration-300 hover:scale-110 group relative overflow-hidden"
                  style={{ 
                    backgroundColor: '#2d2d30',
                    border: '2px solid #333'
                  }}
                  title={social.label}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = `${social.color}20`;
                    e.target.style.borderColor = social.color;
                    anime({
                      targets: e.target,
                      rotateY: [0, 360],
                      duration: 600,
                      easing: 'easeOutQuart'
                    });
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2d2d30';
                    e.target.style.borderColor = '#333';
                  }}
                >
                  <div className="text-white group-hover:scale-110 transition-transform duration-300">
                    {social.icon}
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Enhanced Quick Links */}
          <div className="footer-float opacity-0">
            <div className="flex items-center gap-2 mb-6">
              <Zap size={20} style={{ color: currentColors.accent }} />
              <h3 className="font-mono text-sm uppercase tracking-wide text-gray-300 font-bold">
                Navigation
              </h3>
            </div>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-all duration-300 text-sm flex items-center gap-2 hover:translate-x-2 group"
                    onClick={(e) => {
                      e.preventDefault();
                      anime({
                        targets: e.target,
                        scale: [1, 1.1, 1],
                        duration: 300,
                        easing: 'easeOutQuart'
                      });
                      document.querySelector(link.href).scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <div 
                      className="w-1 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: currentColors.accent }}
                    />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Enhanced Specializations */}
          <div className="footer-float opacity-0">
            <div className="flex items-center gap-2 mb-6">
              <Code size={20} style={{ color: currentColors.accent }} />
              <h3 className="font-mono text-sm uppercase tracking-wide text-gray-300 font-bold">
                Specializations
              </h3>
            </div>
            <ul className="space-y-3 text-sm text-gray-400">
              {[
                'Data Analysis & Visualization',
                'Full Stack Web Development',
                'Cross Platform Mobile Apps',
                'Performance Optimization'
              ].map((spec, index) => (
                <li key={index} className="flex items-center gap-2 hover:text-white transition-colors duration-300 hover:translate-x-1">
                  <div 
                    className="w-1 h-1 rounded-full"
                    style={{ backgroundColor: currentColors.accent }}
                  />
                  {spec}
                </li>
              ))}
            </ul>
          </div>

          {/* Enhanced Contact Info */}
          <div className="footer-float opacity-0">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles size={20} style={{ color: currentColors.accent }} />
              <h3 className="font-mono text-sm uppercase tracking-wide text-gray-300 font-bold">
                Let's Work Together
              </h3>
            </div>
            <div className="space-y-4 text-sm text-gray-400">
              <div className="flex items-center gap-2 hover:text-white transition-colors duration-300">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: currentColors.accent }}
                />
                {personalInfo.email}
              </div>
              <div className="flex items-center gap-2 hover:text-white transition-colors duration-300">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: currentColors.accent }}
                />
                {personalInfo.location}
              </div>
              <div className="flex items-center gap-2 mt-6 p-3 rounded-lg" style={{ backgroundColor: `${currentColors.accent}15` }}>
                <div 
                  className="w-3 h-3 rounded-full animate-pulse"
                  style={{ backgroundColor: currentColors.accent }}
                />
                <span className="text-white font-semibold">Available for projects</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Bottom Section */}
        <div className="border-t border-gray-800 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-sm text-gray-400 flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: currentColors.accent }}
              />
              © 2024 Kevin Panchal. All rights reserved.
            </div>
            
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <span>Built with</span>
              <Heart size={14} className="text-red-500 animate-pulse" />
              <span>using</span>
              <div className="flex items-center gap-2">
                {techStack.map((tech, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: `${currentColors.accent}20` }}
                  >
                    <div style={{ color: currentColors.accent }}>
                      {tech.icon}
                    </div>
                    <span className="text-xs font-semibold" style={{ color: currentColors.accent }}>
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={scrollToTop}
              className="scroll-top-btn flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 p-3 rounded-full"
              style={{ backgroundColor: `${currentColors.accent}20` }}
            >
              <span>Back to Top</span>
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Animated Background Elements with Enhanced Movement */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-5">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="footer-particle absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              color: currentColors.accent
            }}
          >
            <Sparkles size={Math.random() * 8 + 8} />
          </div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;